// src/features/hotels/hooks/useHotelPermissions.ts

import { usePermissions } from 'react-admin';
import { hasResourcePermission } from '@/config/resourceConfig';
import type { UserRole } from '@/types/models';

interface HotelPermissions {
  canList: boolean;
  canShow: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canForceDelete: boolean;
  canViewAll: boolean;
  canViewStats: boolean;
}

/**
 * Hook to get hotel-specific permissions for the current user
 *
 * Business Rules:
 * - Only SuperAdmin can CREATE, EDIT, DELETE hotels
 * - SuperAdmin and GroupAdmin can LIST hotels (filtered by assignments)
 * - SuperAdmin can view all hotels without filters
 */
export const useHotelPermissions = (): HotelPermissions => {
  const { permissions } = usePermissions();
  const role = permissions as UserRole;
  const isSuperAdmin = role === 'SuperAdmin' || role === 'Admin';

  return {
    canList: hasResourcePermission('hotels', 'list'),
    canShow: hasResourcePermission('hotels', 'show'),
    canCreate: hasResourcePermission('hotels', 'create'),
    canEdit: hasResourcePermission('hotels', 'edit'),
    canDelete: hasResourcePermission('hotels', 'delete'),
    canForceDelete: isSuperAdmin,
    canViewAll: isSuperAdmin,
    canViewStats: isSuperAdmin,
  };
};